/**
 * Partner work in Setup: what the case agent asks a partner to do — on one
 * subject in one country, the Backoffice components named, the partners who
 * did it before (with how often, for whom, on what terms) — approved as an
 * assignment with the partner and the terms (an active partner locked in,
 * dispatched at Start — or at once, after it) or declined; the client files
 * the partner gets, as the ask names them; the partner's form as the case
 * agent pre-filled it, once built (each partner's copy is cut from it at
 * Start); and the assignments on the case — after Start, each dispatched
 * one's Backoffice request, Dispatch (again) where it has none, Re-assign to
 * another partner, and its terms (plans/workbench2/05-m2-close-out.md §1,
 * decisions 11–14).
 */

import { useState } from 'react';

import {
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import {
  delivered,
  type SetupAssignment,
  type SetupCandidate,
  type SetupPartnerAsk,
  type SetupPrefill,
  type WorkbenchSetup,
} from '@/types/workbench';
import { formatMoney } from '@/utils';
import { toError } from '@/utils/errors';
import { AgentText } from '../AgentText';
import { openWorkbenchPrefill } from '../FilesView';
import { refusalMessage, useSetupActions } from './setupActions';

function deadline(dueDate: string | null, days: number | null): string | null {
  if (dueDate) {
    return `due ${dueDate}`;
  }
  return days != null ? `${days} business days` : null;
}

/** How often a candidate did the work there, and for whom. */
export function history(candidate: SetupCandidate, place: string | null): string {
  const { clientCases, otherCases } = candidate;
  const total = clientCases + otherCases;
  if (total === 0) {
    return `lists ${place ?? 'the country'} in its scope`;
  }
  const cases = `${total} ${total === 1 ? 'case' : 'cases'}`;
  let whose = `${cases} (${clientCases} for this client)`;
  if (otherCases === 0) {
    whose = `${cases} for this client`;
  } else if (clientCases === 0) {
    whose = `${cases} for other clients`;
  }
  return candidate.sameWork ? whose : `${whose} — other work there`;
}

function Candidates({
  ask,
  currency,
}: Readonly<{ ask: SetupPartnerAsk; currency: string | null }>) {
  const [first, ...rest] = ask.candidates;
  if (!first) {
    return (
      <Text size="xs" c="dimmed">
        {ask.jurisdiction
          ? `No partner did this work in ${ask.jurisdiction} before, and none lists it.`
          : 'No country to look partners up by.'}
      </Text>
    );
  }
  const terms = [
    first.fee ? formatMoney(first.fee, first.currency ?? currency) : null,
    first.turnaroundDays != null ? `about ${first.turnaroundDays} days` : null,
    history(first, ask.jurisdiction),
    first.active ? null : 'not active: shortlisted only',
  ].filter(Boolean);
  return (
    <>
      <Text size="xs" data-testid={`setup-proposed-${ask.interventionId}`}>
        Best placed: {first.name ?? 'a partner'} — {terms.join(' · ')}
      </Text>
      {rest.length > 0 && (
        <Text size="xs" c="dimmed">
          Also:{' '}
          {rest.map((c) => `${c.name ?? 'a partner'} (${history(c, ask.jurisdiction)})`).join('; ')}
        </Text>
      )}
    </>
  );
}

type Acting = 'approve' | 'decline' | null;

/** Partner work approved as an assignment — or declined. */
function PartnerActions({
  ask,
  currency,
}: Readonly<{ ask: SetupPartnerAsk; currency: string | null }>) {
  const actions = useSetupActions();
  const onCase = () => ask.files.filter((f) => f.onCase).map((f) => f.key);
  const first = ask.candidates[0];
  const [open, setOpen] = useState<Acting>(null);
  const [partner, setPartner] = useState<string | null>(first?.networkPartnerId ?? null);
  const [fee, setFee] = useState<string | number>(first?.fee ?? '');
  const [days, setDays] = useState<string | number>(first?.turnaroundDays ?? '');
  const [note, setNote] = useState('');
  const [chosen, setChosen] = useState<string[]>(onCase);
  if (!actions.partnerWorkEditable || ask.status !== 'open') {
    return null;
  }
  // Each time, the terms the first candidate did the work on.
  const start = (what: Acting) => {
    setPartner(first?.networkPartnerId ?? null);
    setFee(first?.fee ?? '');
    setDays(first?.turnaroundDays ?? '');
    setNote('');
    setChosen(onCase());
    setOpen(what);
  };
  if (open === null) {
    return (
      <Group gap={4}>
        <Button
          size="compact-xs"
          onClick={() => start('approve')}
          data-testid={`setup-approve-${ask.interventionId}`}
        >
          Approve…
        </Button>
        <Button
          size="compact-xs"
          variant="default"
          onClick={() => start('decline')}
          data-testid={`setup-decline-${ask.interventionId}`}
        >
          Decline…
        </Button>
      </Group>
    );
  }
  const name = `partner:${ask.interventionId}`;
  const save = async () => {
    const saved =
      open === 'approve'
        ? await actions.run(
            name,
            () =>
              vnextApiClient.approveSetupPartnerWork(actions.reportId, ask.interventionId, {
                networkPartnerId: partner ?? '',
                npFee: fee === '' ? null : String(fee),
                npCurrency: currency,
                turnaroundTimeDays: days === '' ? null : Number(days),
                note: note.trim() || null,
                files: ask.files.length > 0 ? chosen : null,
              }),
            actions.started
              ? 'Assignment made and dispatched to the partner.'
              : 'Assignment made — dispatched at Start.'
          )
        : await actions.run(
            name,
            () =>
              vnextApiClient.declineSetupPartnerWork(
                actions.reportId,
                ask.interventionId,
                note.trim() || null
              ),
            'Declined — the case agent carries on without it.'
          );
    if (saved) {
      setOpen(null);
    }
  };
  return (
    <Stack gap={4} data-testid={`setup-partner-form-${ask.interventionId}`}>
      {open === 'approve' && (
        <Group gap="xs" align="flex-end">
          <Select
            size="xs"
            label="Partner"
            placeholder="A candidate"
            data={ask.candidates.map((c) => ({
              value: c.networkPartnerId,
              label: `${c.name ?? 'A partner'}${c.active ? '' : ' (not active)'}`,
            }))}
            value={partner}
            onChange={setPartner}
            w={220}
            data-testid="setup-approve-partner"
          />
          <NumberInput
            size="xs"
            label={`Fee${currency ? ` (${currency})` : ''}`}
            min={0}
            decimalScale={2}
            value={fee}
            onChange={setFee}
            w={120}
            data-testid="setup-approve-fee"
          />
          <NumberInput
            size="xs"
            label="Turnaround (days)"
            min={0}
            value={days}
            onChange={setDays}
            w={130}
          />
        </Group>
      )}
      {open === 'approve' && <FilesChoice ask={ask} chosen={chosen} onChange={setChosen} />}
      <Group gap="xs" align="flex-end">
        <TextInput
          size="xs"
          placeholder={open === 'approve' ? 'A note for the assignment' : 'Why (optional)'}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 180 }}
        />
        <Button
          size="xs"
          disabled={open === 'approve' && !partner}
          loading={actions.pending === name}
          onClick={() => void save()}
          data-testid="setup-partner-save"
        >
          {open === 'approve' ? 'Approve as an assignment' : 'Decline'}
        </Button>
        <Button size="xs" variant="subtle" onClick={() => setOpen(null)}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}

function onDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** The partner's form pre-filled for an assignment — its copy, put on the
 * partner's request at Start — and Deliver where it hasn't reached it (a
 * refusal says why). A person's click: nothing reaches a partner otherwise. */
function PrefillRow({ assignment }: Readonly<{ assignment: SetupAssignment }>) {
  const actions = useSetupActions();
  const [busy, setBusy] = useState(false);
  const { prefill } = assignment;
  if (!prefill) {
    return null;
  }
  const tables = `${prefill.copies} ${prefill.copies === 1 ? 'table' : 'tables'}`;
  const state =
    prefill.status === 'delivered'
      ? `delivered${prefill.deliveredAt ? ` ${onDay(prefill.deliveredAt)}` : ''}`
      : prefill.deliveryError
        ? `not delivered — ${prefill.deliveryError}`
        : 'not delivered yet';
  const deliver = async () => {
    setBusy(true);
    try {
      const done = await vnextApiClient.deliverPrefill(actions.reportId, prefill.id);
      notifications.show({
        color: delivered(done) ? 'teal' : 'orange',
        message: delivered(done)
          ? "Delivered to the partner's request."
          : `Not delivered${done.detail ? ` — ${done.detail}` : ''}.`,
      });
    } catch (err) {
      if (!(err instanceof ApiConflictError)) {
        Sentry.captureException(toError(err));
      }
      notifications.show({ color: 'red', message: refusalMessage(err) });
    } finally {
      setBusy(false);
    }
    await actions.refresh();
  };
  return (
    <Group gap={6} data-testid={`setup-prefill-${assignment.id}`}>
      <Text size="xs" c="dimmed">
        Partner&apos;s form: pre-filled copy ({tables}) · {state}
      </Text>
      {prefill.status !== 'delivered' && (
        <Button
          size="compact-xs"
          variant="default"
          loading={busy}
          onClick={() => void deliver()}
          data-testid={`setup-deliver-${assignment.id}`}
        >
          Deliver
        </Button>
      )}
    </Group>
  );
}

/** The case's own pre-fill of the partner's form, with Open. */
function FormLine({ prefill }: Readonly<{ prefill: SetupPrefill | null }>) {
  const actions = useSetupActions();
  const [busy, setBusy] = useState(false);
  if (!prefill) {
    return null;
  }
  const tables = `${prefill.copies} ${prefill.copies === 1 ? 'table' : 'tables'}`;
  const open = async () => {
    setBusy(true);
    try {
      await openWorkbenchPrefill(actions.reportId, prefill.id);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack gap={4} data-testid="setup-form">
      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
        The partner&apos;s form
      </Text>
      <Group gap={6}>
        <Text size="sm">
          Pre-filled by the case agent
          {prefill.createdAt ? ` on ${onDay(prefill.createdAt)}` : ''} ({tables}). Each
          partner&apos;s copy is cut from it and delivered at Start.
        </Text>
        <Button
          size="compact-xs"
          variant="default"
          loading={busy}
          onClick={() => void open()}
          data-testid="setup-form-open"
        >
          Open
        </Button>
      </Group>
    </Stack>
  );
}

/** The client files the partner gets, as the ask names them (decision 14):
 * each ticked to start with; none named, every client file as today. */
function FilesChoice({
  ask,
  chosen,
  onChange,
}: Readonly<{ ask: SetupPartnerAsk; chosen: string[]; onChange: (keys: string[]) => void }>) {
  if (ask.files.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        The partner gets every client file on the request.
      </Text>
    );
  }
  return (
    <Stack gap={2} data-testid="setup-approve-files">
      <Text size="xs" c="dimmed">
        The partner gets these files — untick any they should not see:
      </Text>
      {ask.files.map((f) => (
        <Checkbox
          key={f.key}
          size="xs"
          label={`${f.key} · ${f.filename ?? 'a file'}${f.onCase ? '' : ' (no longer on the case)'}`}
          disabled={!f.onCase}
          checked={chosen.includes(f.key)}
          onChange={(e) =>
            onChange(
              e.currentTarget.checked ? [...chosen, f.key] : chosen.filter((k) => k !== f.key)
            )
          }
          data-testid={`setup-approve-file-${f.key}`}
        />
      ))}
    </Stack>
  );
}

function candidatesFor(
  assignment: SetupAssignment,
  asks: SetupPartnerAsk[]
): { value: string; label: string }[] {
  const own = asks.find((a) => a.assignmentId === assignment.id);
  const place = assignment.items[0]?.jurisdiction ?? null;
  const pool = own
    ? own.candidates
    : asks.filter((a) => a.jurisdiction === place).flatMap((a) => a.candidates);
  const seen = new Set<string>();
  return pool
    .filter((c) => c.networkPartnerId !== assignment.networkPartnerId)
    .filter((c) => (seen.has(c.networkPartnerId) ? false : (seen.add(c.networkPartnerId), true)))
    .map((c) => ({
      value: c.networkPartnerId,
      label: `${c.name ?? 'A partner'}${c.active ? '' : ' (not active)'}`,
    }));
}

/** The work moved to another partner (decision 13): the request cancelled
 * with the reason, a new assignment with the same items locked to the
 * partner chosen at the terms given — a term left blank keeps the old one —
 * dispatched and its copy delivered. */
function ReassignForm({
  assignment,
  asks,
  currency,
  onDone,
}: Readonly<{
  assignment: SetupAssignment;
  asks: SetupPartnerAsk[];
  currency: string | null;
  onDone: () => void;
}>) {
  const actions = useSetupActions();
  const [partner, setPartner] = useState<string | null>(null);
  const [fee, setFee] = useState<string | number>('');
  const [days, setDays] = useState<string | number>('');
  const [reason, setReason] = useState('');
  const name = `reassign:${assignment.id}`;
  const ready = Boolean(partner) && reason.trim().length >= 10;
  const save = async () => {
    const saved = await actions.run(
      name,
      () =>
        vnextApiClient.reassignSetupPartnerWork(actions.reportId, assignment.id, {
          networkPartnerId: partner ?? '',
          reason: reason.trim(),
          npFee: fee === '' ? null : String(fee),
          npCurrency: fee === '' ? null : (assignment.currency ?? currency),
          turnaroundTimeDays: days === '' ? null : Number(days),
        }),
      'Re-assigned — the new request dispatched and its copy of the form delivered.'
    );
    if (saved) {
      onDone();
    }
  };
  return (
    <Stack gap={4} data-testid={`setup-reassign-form-${assignment.id}`}>
      <Group gap="xs" align="flex-end">
        <Select
          size="xs"
          label="Partner"
          placeholder="Another partner"
          data={candidatesFor(assignment, asks)}
          value={partner}
          onChange={setPartner}
          w={220}
          data-testid="setup-reassign-partner"
        />
        <NumberInput
          size="xs"
          label={`Fee${(assignment.currency ?? currency) ? ` (${assignment.currency ?? currency})` : ''}`}
          placeholder={assignment.fee ?? 'as before'}
          min={0}
          decimalScale={2}
          value={fee}
          onChange={setFee}
          w={120}
          data-testid="setup-reassign-fee"
        />
        <NumberInput
          size="xs"
          label="Turnaround (days)"
          placeholder={
            assignment.turnaroundDays != null ? String(assignment.turnaroundDays) : 'as before'
          }
          min={0}
          value={days}
          onChange={setDays}
          w={130}
        />
      </Group>
      <Group gap="xs" align="flex-end">
        <TextInput
          size="xs"
          placeholder="Why (the partner is told; ten characters at least)"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 220 }}
          data-testid="setup-reassign-reason"
        />
        <Button
          size="xs"
          disabled={!ready}
          loading={actions.pending === name}
          onClick={() => void save()}
          data-testid="setup-reassign-save"
        >
          Re-assign
        </Button>
        <Button size="xs" variant="subtle" onClick={onDone}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}

/** A partner's fee and deadline moved after dispatch (decision 12), pushed
 * onto the Backoffice request. */
function TermsForm({
  assignment,
  currency,
  onDone,
}: Readonly<{ assignment: SetupAssignment; currency: string | null; onDone: () => void }>) {
  const actions = useSetupActions();
  const [fee, setFee] = useState<string | number>(assignment.fee ?? '');
  const [days, setDays] = useState<string | number>(assignment.turnaroundDays ?? '');
  const [reason, setReason] = useState('');
  const name = `terms:${assignment.id}`;
  const save = async () => {
    const saved = await actions.run(
      name,
      () =>
        vnextApiClient.updateAssignmentTerms(assignment.id, {
          npFee: fee === '' ? null : String(fee),
          npCurrency: assignment.currency ?? currency,
          turnaroundTimeDays: days === '' ? null : Number(days),
          reason: reason.trim() || null,
        }),
      "Terms updated and pushed to the partner's request."
    );
    if (saved) {
      onDone();
    }
  };
  return (
    <Group gap="xs" align="flex-end" data-testid={`setup-terms-form-${assignment.id}`}>
      <NumberInput
        size="xs"
        label={`Fee${(assignment.currency ?? currency) ? ` (${assignment.currency ?? currency})` : ''}`}
        min={0}
        decimalScale={2}
        value={fee}
        onChange={setFee}
        w={120}
        data-testid="setup-terms-fee"
      />
      <NumberInput
        size="xs"
        label="Turnaround (days)"
        min={0}
        value={days}
        onChange={setDays}
        w={130}
      />
      <TextInput
        size="xs"
        placeholder="Why (optional)"
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        style={{ flex: 1, minWidth: 160 }}
      />
      <Button
        size="xs"
        loading={actions.pending === name}
        onClick={() => void save()}
        data-testid="setup-terms-save"
      >
        Save terms
      </Button>
      <Button size="xs" variant="subtle" onClick={onDone}>
        Cancel
      </Button>
    </Group>
  );
}

/** After Start: an assignment's Backoffice request — Dispatch where it has
 * none (a partner added late) or Dispatch again where Backoffice refused,
 * Re-assign to another partner, and the terms moved on the request. */
function AssignmentActions({
  assignment,
  asks,
  currency,
}: Readonly<{ assignment: SetupAssignment; asks: SetupPartnerAsk[]; currency: string | null }>) {
  const actions = useSetupActions();
  const [open, setOpen] = useState<'reassign' | 'terms' | null>(null);
  if (!actions.started || assignment.outcome === 'cancelled') {
    return null;
  }
  if (actions.evaluationCopy) {
    return (
      <Text size="xs" c="dimmed" data-testid={`setup-not-dispatched-${assignment.id}`}>
        Not dispatched — an evaluation copy: nothing goes to the Backoffice.
      </Text>
    );
  }
  const dispatchable =
    actions.partnerWorkEditable &&
    (assignment.syncError != null || (assignment.ready && assignment.npRequestId == null));
  const dispatched = assignment.npRequestId != null;
  const dispatch = () =>
    actions.run(
      `dispatch:${assignment.id}`,
      () => vnextApiClient.dispatchSetupPartnerWork(actions.reportId, assignment.id),
      "Dispatched — the partner's copy of the form delivered to the request."
    );
  return (
    <Stack gap={4}>
      {assignment.syncError ? (
        <Text size="xs" c="orange.8" data-testid={`setup-sync-error-${assignment.id}`}>
          Not dispatched — {assignment.syncError}
        </Text>
      ) : null}
      {open === null ? (
        <Group gap={4}>
          {dispatchable ? (
            <Button
              size="compact-xs"
              loading={actions.pending === `dispatch:${assignment.id}`}
              onClick={() => void dispatch()}
              data-testid={`setup-dispatch-${assignment.id}`}
            >
              {assignment.syncError ? 'Dispatch again' : 'Dispatch'}
            </Button>
          ) : null}
          {dispatched && actions.partnerWorkEditable ? (
            <Button
              size="compact-xs"
              variant="default"
              onClick={() => setOpen('reassign')}
              data-testid={`setup-reassign-${assignment.id}`}
            >
              Re-assign…
            </Button>
          ) : null}
          {dispatched && actions.commercialsEditable ? (
            <Button
              size="compact-xs"
              variant="default"
              onClick={() => setOpen('terms')}
              data-testid={`setup-terms-${assignment.id}`}
            >
              Edit terms…
            </Button>
          ) : null}
        </Group>
      ) : null}
      {open === 'reassign' ? (
        <ReassignForm
          assignment={assignment}
          asks={asks}
          currency={currency}
          onDone={() => setOpen(null)}
        />
      ) : null}
      {open === 'terms' ? (
        <TermsForm assignment={assignment} currency={currency} onDone={() => setOpen(null)} />
      ) : null}
    </Stack>
  );
}

/** What came of an ask for partner work. */
export function outcome(ask: SetupPartnerAsk): string {
  if (ask.status === 'open') {
    return 'Waiting for you';
  }
  if (ask.status === 'cancelled') {
    return 'Withdrawn';
  }
  return ask.answer?.approved === false ? 'Declined' : 'Approved';
}

export function SetupPartners({ setup }: Readonly<{ setup: WorkbenchSetup }>) {
  const { asks, assignments } = setup.partners;
  const currency = setup.price.price?.currency ?? setup.price.suggested?.currency ?? null;
  const names = new Map(setup.subjects.map((s) => [s.id, s.name]));
  const byId = new Map(assignments.map((a) => [a.id, a]));
  return (
    <Stack gap="sm" data-testid="setup-partners">
      <Stack gap={4}>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          Asked for
        </Text>
        {asks.length === 0 ? (
          <Text size="sm" c="dimmed">
            The case agent has asked for no partner work.
          </Text>
        ) : (
          asks.map((ask) => {
            const assigned = ask.assignmentId ? byId.get(ask.assignmentId) : undefined;
            return (
              <Stack
                key={ask.interventionId}
                gap={2}
                data-testid={`setup-partner-work-${ask.interventionId}`}
              >
                <Group gap={6}>
                  <Text size="sm">
                    {ask.components.length > 0 ? ask.components.join(', ') : 'Partner work'}
                    {' on '}
                    {ask.subjectId ? (names.get(ask.subjectId) ?? 'a subject') : 'the case'}
                    {ask.jurisdiction ? ` in ${ask.jurisdiction}` : ''}
                  </Text>
                  <Badge
                    size="xs"
                    variant="light"
                    color={ask.status === 'open' ? 'orange' : 'gray'}
                    style={{ textTransform: 'none' }}
                  >
                    {outcome(ask)}
                  </Badge>
                </Group>
                {ask.question ? (
                  <Text size="xs" style={{ whiteSpace: 'pre-line' }}>
                    <AgentText text={ask.question} />
                  </Text>
                ) : null}
                {ask.reason ? (
                  <Text size="xs" c="dimmed">
                    <AgentText text={ask.reason} />
                  </Text>
                ) : null}
                {assigned?.partner ? (
                  <Text size="xs" c="dimmed">
                    Assigned to {assigned.partner}
                  </Text>
                ) : null}
                <Candidates ask={ask} currency={currency} />
                <PartnerActions ask={ask} currency={currency} />
              </Stack>
            );
          })
        )}
      </Stack>
      <FormLine prefill={setup.partners.prefill} />
      <Stack gap={4}>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          Assignments
        </Text>
        {assignments.length === 0 ? (
          <Text size="sm" c="dimmed">
            No partner assigned yet.
          </Text>
        ) : (
          assignments.map((a) => {
            const terms = [
              a.fee ? formatMoney(a.fee, a.currency) : null,
              deadline(a.dueDate, a.turnaroundDays),
            ].filter(Boolean);
            const components = [...new Set(a.items.map((i) => i.component).filter(Boolean))];
            return (
              <Stack key={a.id} gap={0} data-testid={`setup-assignment-${a.id}`}>
                <Group gap={6}>
                  <Text size="sm" fw={500}>
                    {a.partner ?? 'No partner locked in'}
                  </Text>
                  {a.ready ? (
                    <Badge size="xs" variant="light" color="teal" style={{ textTransform: 'none' }}>
                      ready to dispatch
                    </Badge>
                  ) : a.outcome === 'cancelled' ? (
                    <Badge size="xs" variant="light" color="gray">
                      Cancelled
                    </Badge>
                  ) : (
                    <Badge
                      size="xs"
                      variant="light"
                      color="orange"
                      style={{ textTransform: 'none' }}
                    >
                      {a.status.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {components.length > 0 ? components.join(', ') : 'No components'}
                  {terms.length > 0 ? ` · ${terms.join(' · ')}` : ''}
                  {a.npRequestId != null ? ` · Backoffice request ${a.npRequestId}` : ''}
                </Text>
                <PrefillRow assignment={a} />
                <AssignmentActions assignment={a} asks={asks} currency={currency} />
              </Stack>
            );
          })
        )}
      </Stack>
    </Stack>
  );
}
